<template>
  <main class="diary-templates">
    <div class="diary-templates__container">
      <section>
        <div class="diary-templates__header">
          <h1 class="diary-templates__title">Шаблоны протоколов лечения</h1>
          <button
            class="diary-templates__btn"
            v-on:click="onShowDialogForm"
            type="button">
            Добавить
          </button>
        </div>
        <div class="service-page__header">
          <div class="service-page__header__left">
            <input-text-search
              class="service-page__search"
              placeholder="Введите название шаблона"
              v-bind:violations="searchViolations"
              v-model="searchQuery"
              v-on:input="searchTemplateList" />
          </div>
        </div>
        <side-slide-transition>
          <confirm-side-dialog
            v-if="shownDialogForm"
            class="diary-templates-dialog"
            v-on:cancel="onCloseDialogForm"
            v-bind:label="formDialogTitle">
            <input-select
              class="diary-templates-dialog__input-select"
              title="Тип"
              v-if="!isEdit"
              v-bind:options="formTypes"
              v-model="selectedForm"
            />
            <form-visit-diary-template
              v-if="selectedForm === $options.templateForm"
              class="diary-templates__edit__form"
              v-bind:id="template.id"
              v-bind:disease-list="diseases"
              v-bind:category-list="categoryOptions"
              v-bind:initial-category-id="template.categoryId"
              v-bind:initial-complaints="template.complaints"
              v-bind:initial-dental-anamnesis="template.dentalAnamnesis"
              v-bind:initial-diagnosis="template.diagnosis"
              v-bind:initial-general-analysis="template.generalAnalysis"
              v-bind:initial-guarantees="template.guarantees"
              v-bind:initial-icd-id="template.icdId"
              v-bind:initial-icd-name="template.icdName"
              v-bind:initial-name="template.name"
              v-bind:initial-objectively="template.objectively"
              v-bind:initial-recommendations="template.recommendations"
              v-bind:initial-studies="template.studies"
              v-bind:initial-treatment="template.treatment"
              v-bind:initial-sanitized="template.sanitized"
              v-bind:initial-teeth="template.teeth"
              v-bind:input-errors="inputErrors"
              v-bind:is-diseases-load="isDiseasesLoad"
              v-on:query-disease="fetchDiseases"
              v-on:submit="onSubmitDiaryTemplate"
            >
              <template slot="buttons">
                <button
                  type="button"
                  v-on:click="onCloseDialogForm"
                  class="visit-diary-template__button remove">
                  Отмена
                </button>
              </template>
            </form-visit-diary-template>
            <div class="diary-templates__edit__form">
              <form
                v-if="selectedForm === $options.categoryForm"
                class="visit-diary-template"
                v-on:submit.prevent="onSubmitDiaryTemplateCategory"
              >
                <input-text
                  title="Название"
                  v-model="category.name" />
                <div class="visit-diary-template__buttons">
                  <button
                    type="button"
                    v-on:click="onCloseDialogForm"
                    class="visit-diary-template__button remove">
                    Отмена
                  </button>
                  <button
                    class="visit-diary-template__button save"
                    type="submit"
                  >Сохранить</button>
                </div>
              </form>
            </div>
          </confirm-side-dialog>
        </side-slide-transition>
        <div class="diary-templates__row">
          <div class="diary-templates__categories">
            <div class="diary-templates__categories__caption">Категории</div>
            <div v-if="!isEmptyCategoryList" class="diary-templates__categories__container">
              <category-item
                v-for="item in categoryList"
                v-bind:category="item"
                v-bind:has-delete="true"
                v-bind:has-edit="true"
                v-bind:key="item.id"
                v-bind:selected-category="!!categoryId ? categoryId : -1"
                v-on:delete-category="onRemoveDiaryTemplateCategory(item.id)"
                v-on:edit-category="onShowEditDialogCategory"
                v-on:select-category="onSelectCategory"
              />
            </div>
          </div>
          <div class="diary-templates__table">
            <div class="diary-templates__table__head">
              <div>Шаблон</div>
            </div>
            <div v-if="!isEmptyCategoryList">
              <button
                class="diary-templates__table__row"
                type="button"
                v-for="(item, index) in templates"
                v-bind:key="index"
                v-on:click="onShowEditDialogTemplate(item)"
              >
                <div class="diary-templates__table__row__name">{{ item.name }}</div>
                <button
                  class="diary-templates__table__row__edit"
                  type="button"
                  v-on:click.prevent.stop="onShowEditDialogTemplate(item)"
                />
                <button
                  class="diary-templates__table__row__delete"
                  type="button"
                  v-on:click.prevent.stop="onRemoveDiaryTemplate(item.id)"
                />
              </button>
            </div>
          </div>
        </div>
        <div v-if="isEmptyCategoryList" class="diary-templates__empty-templates">
          Шаблонов с таким наименованием не найдено
        </div>
      </section>
    </div>
  </main>
</template>
<script>
import FormVisitDiaryTemplate from '@/components/FormVisitDiaryTemplate.vue';
import notifierMixin from '@/mixins/notifierMixin';
import { builtInDialogMixin, DialogResult } from '@/components/ClinicPanelDialog.vue';
import SideSlideTransition from '@/components/SideSlideTransition.vue';
import ConfirmSideDialog from '@/components/ConfirmSideDialog.vue';
import CategoryItem from '@/components/CategoryItem.vue';
import InputSelect from '@/components/InputSelect.vue';
import InputText from '@/components/InputText.vue';
import InputTextSearch from '@/components/InputTextSearch.vue';
import debounce from 'lodash.debounce';

/**
 * @description Шаблон протокола лечения.
 * @param {number|null} id
 * @param {string} name
 * @param {number|null} categoryId
 * @param {string} complaints
 * @param {string} dentalAnamnesis
 * @param {string} diagnosis
 * @param {string} generalAnalysis
 * @param {string} guarantees
 * @param {number|null} icdId
 * @param {string} icdName
 * @param {string} objectively
 * @param {string} prognosis
 * @param {string} recommendations
 * @param {string} studies
 * @param {string} treatment
 * @param {array}  teeth
 * @param {array}  dentalParts
 * @param {boolean} sanitized
 * @constructor
 */
function DiaryTemplate(
  id = null,
  name = '',
  categoryId = null,
  complaints = '',
  dentalAnamnesis = '',
  diagnosis = '',
  generalAnalysis = '',
  guarantees = '',
  icdId = null,
  icdName = '',
  objectively = '',
  prognosis = '',
  recommendations = '',
  studies = '',
  treatment = '',
  teeth = [],
  dentalParts = [],
  sanitized = false,
) {
  this.id = id;
  this.name = name;
  this.categoryId = categoryId;
  this.complaints = complaints;
  this.dentalAnamnesis = dentalAnamnesis;
  this.diagnosis = diagnosis;
  this.generalAnalysis = generalAnalysis;
  this.guarantees = guarantees;
  this.icdId = icdId;
  this.icdName = icdName;
  this.objectively = objectively;
  this.prognosis = prognosis;
  this.recommendations = recommendations;
  this.studies = studies;
  this.treatment = treatment;
  this.teeth = teeth;
  this.dentalParts = dentalParts;
  this.sanitized = sanitized;
}

/**
 * @description Категория.
 * @param id
 * @param name
 * @constructor
 */
function Category(
  id = null,
  name = '',
) {
  this.id = id;
  this.name = name;
}

/**
 * @description Страница шаблонов протокола приема.
 * @type {Vue}
 */
export default {
  categoryForm: 'categoryForm',
  templateForm: 'templateForm',
  components: {
    InputText,
    InputSelect,
    CategoryItem,
    ConfirmSideDialog,
    SideSlideTransition,
    FormVisitDiaryTemplate,
    InputTextSearch,
  },
  computed: {
    formDialogTitle() {
      return this.template.id !== null || this.category.id !== null
        ? 'Редактировать'
        : 'Добавить';
    },
    isSearchQueryInvalid() {
      return this.searchQuery.length < 3;
    },
    isEmptyCategoryList() {
      return this.isAwaitCategoryRequest !== true && this.categoryList.length === 0;
    },
  },
  created() {
    this.fetchCategories().then(() => {
      this.categoryOptions = this.categoryList.slice();
    });
  },
  data() {
    return {
      /**
       * @description Редактируется ли.
       * @type {Boolean}
       */
      isEdit: false,
      /**
       * @description поисковая строка по услугам
       * @type {String}
       */
      searchQuery: '',
      /**
       * @description Ошибки в поисковом запросе
       * @type {Array}
       */
      searchViolations: [],
      /**
       * @description Опции категорий.
       * @type {any[]}
       */
      categoryOptions: [],
      categoryList: [],
      diseases: [],
      expandedPanelIndex: -1,
      isDiseasesLoad: false,
      shownDialogForm: false,
      templates: [],
      template: new DiaryTemplate(),
      category: new Category(),
      categoryId: null,
      selectedForm: null,
      formTypes: [
        {
          title: 'Категория',
          value: this.$options.categoryForm,
        },
        {
          title: 'Шаблон',
          value: this.$options.templateForm,
        },
      ],
      inputErrors: {},
      isAwaitCategoryRequest: false,
    };
  },
  methods: {
    /**
     * @description выполнить поиск
     */
    searchTemplateList: debounce(async function _() {
      this.searchViolations = [];
      if (this.searchQuery.length === 0) {
        this.fetchCategories();
        return;
      }
      if (this.isSearchQueryInvalid) {
        this.searchViolations.push('Необходимо ввести не менее 3-х символов');
        return;
      }
      this.fetchCategories(this.searchQuery);
    }, 300),
    /**
     * @description Получить список категорий.
     * @returns {Promise}
     */
    fetchCategories(search = '') {
      this.isAwaitCategoryRequest = true;
      return this
        .$visitGateway
        .getDiaryTemplateCategoryList(search)
        .then((list) => {
          this.categoryList = list;
          const first = this.categoryList[0];
          if (first) {
            this.fetchTemplates(first.id, search);
          }
        })
        .finally(() => { this.isAwaitCategoryRequest = false; });
    },
    /**
     * @description Получить список заболевание по МКБ-10.
     * @param {string} query Поиск.
     * @returns {void}
     */
    fetchDiseases(query = '') {
      this.isDiseasesLoad = true;
      if (this.timerId) {
        clearTimeout(this.timerId);
      }
      this.timerId = setTimeout(async () => {
        (await this.$visitGateway.viewDiseaseList(query))
          .forEach((list) => { this.diseases = list; });
        this.isDiseasesLoad = false;
      }, 1000);
    },
    /**
     * @description Получить шаблоны протокола приема.
     * @param {number} categoryId
     * @param {string} search
     * @returns {void}
     */
    fetchTemplates(categoryId, search = '') {
      this.categoryId = categoryId;
      this
        .$visitGateway
        .getDiaryTemplatesByCategory(categoryId, search)
        .then((list) => { this.templates = list; });
    },
    /**
     * @description Обработать выбор категории.
     * @param {number} categoryId Идентификатор категории.
     * @returns {void}
     */
    onSelectCategory(categoryId) {
      this.fetchTemplates(categoryId, this.isSearchQueryInvalid ? '' : this.searchQuery);
    },
    /**
     * @description Обработать показ диалогового окна категории.
     * @param {number} categoryId
     * @returns {void}
     */
    onShowEditDialogCategory(categoryId) {
      const category = this.categoryList.find((item) => item.id === categoryId);
      this.category = new Category(category.id, category.name);
      this.selectedForm = this.$options.categoryForm;
      this.isEdit = true;
      this.onShowDialogForm();
    },
    /**
     * @description Обработать показ диалогового окна шаблона протокола лечения.
     * @param {Object} template
     * @returns {void}
     */
    onShowEditDialogTemplate(template) {
      this.selectedForm = this.$options.templateForm;
      this.template = new DiaryTemplate(
        template.id,
        template.name,
        template.category_id,
        template.complaints,
        template.dental_anamnesis,
        template.diagnosis,
        template.general_analysis,
        template.guarantees,
        template.icd_id,
        template.icd_name,
        template.objectively,
        template.prognosis,
        template.recommendations,
        template.studies,
        template.treatment,
        template.teeth,
        template.dental_parts,
        false,
      );
      this.isEdit = true;
      this.onShowDialogForm();
    },
    /**
     * @description Обработать закрытие диалогового окна форм.
     * @returns {void}
     */
    onCloseDialogForm() {
      this.shownDialogForm = false;
      this.category = new Category();
      this.template = new DiaryTemplate();
      this.selectedForm = null;
      this.isEdit = false;
      this.inputErrors = {};
    },
    /**
     * @description Обработать открытие диалогового окна форм.
     * @returns {void}
     */
    onShowDialogForm() {
      if (!this.isEdit) {
        this.selectedForm = this.$options.categoryForm;
      }
      this.category = this.selectedForm === this.$options.categoryForm
        ? this.category
        : (new Category());
      this.template = this.selectedForm === this.$options.templateForm
        ? this.template
        : (new DiaryTemplate());
      this.shownDialogForm = true;
    },
    /**
     * @description Обработать редактирование шаблона плана лечения.
     * @param {DiaryTemplateCreationRequest} request
     * @returns {void}
     */
    onEditDiaryTemplate(request) {
      this
        .$visitGateway
        .editDiaryTemplate(request.getId(), request.toQuery())
        .then((result) => {
          result.forEach(({ title }) => {
            this.notifySuccess(title);
            this.fetchTemplates(this.categoryId);
            this.onCloseDialogForm();
          });
          result.forEachLeft((error) => {
            this.notifyFail(error.getTitle());
            this.inputErrors = error.allCauses();
            this.$nextTick(() => {
              const firstInvalidField = document.querySelector('[aria-invalid="true"]');
              if (firstInvalidField) {
                firstInvalidField.scrollIntoView({ block: 'center', behavior: 'smooth' });
                firstInvalidField.focus({ preventScroll: true });
              }
            });
          });
        });
    },
    /**
     * @description Обработать редактирование категории шаблонов.
     * @returns {void}
     */
    onEditDiaryTemplateCategory() {
      this
        .$visitGateway
        .editDiaryTemplateCategory(this.category.id, this.category.name)
        .then((result) => {
          result.forEach(({ title }) => {
            this.notifySuccess(title);
            this.fetchCategories().then(() => {
              this.categoryOptions = this.categoryList.slice();
            });
            this.onCloseDialogForm();
          });
          result.forEachLeft(({ title }) => {
            this.notifyFail(title);
          });
        });
    },
    /**
     * @description Обработать создание шаблона протокола лечения.
     * @param {DiaryTemplateCreationRequest} request
     * @returns {void}
     */
    onCreateDiaryTemplate(request) {
      this
        .$visitGateway
        .createDiaryTemplate(request.toQuery())
        .then((result) => {
          result.forEach(({ title }) => {
            this.notifySuccess(title);
            this.onCloseDialogForm();
            this.fetchTemplates(this.categoryId);
          });
          result.forEachLeft((error) => {
            this.notifyFail(error.getTitle());
            this.inputErrors = error.allCauses();
            this.$nextTick(() => {
              const firstInvalidField = document.querySelector('[aria-invalid="true"]');
              if (firstInvalidField) {
                firstInvalidField.scrollIntoView({ block: 'center', behavior: 'smooth' });
                firstInvalidField.focus({ preventScroll: true });
              }
            });
          });
        });
    },
    /**
     * @description Обработать создание шаблона протокола лечения.
     * @returns {void}
     */
    onCreateDiaryTemplateCategory() {
      this
        .$visitGateway
        .createDiaryTemplateCategory(this.category.name)
        .then((result) => {
          result.forEach(({ title }) => {
            this.notifySuccess(title);
            this.fetchCategories().then(() => {
              this.categoryOptions = this.categoryList.slice();
            });
            this.onCloseDialogForm();
          });
          result.forEachLeft(({ title }) => {
            this.notifyFail(title);
          });
        });
    },
    /**
     * @description Обработать удаление категории шаблонов.
     * @param {number} categoryId
     * @returns {void}
     */
    async onRemoveDiaryTemplateCategory(categoryId) {
      const dialog = await this.showRemoveAccentDialog('При удалении категории все шаблоны в\u00A0ней будут удалены. Продолжить?');
      if (dialog.result !== DialogResult.DELETE_ACCENT) {
        return;
      }
      this
        .$visitGateway
        .deleteDiaryTemplateCategory(categoryId)
        .then((result) => {
          result.forEach(({ title }) => {
            this.notifySuccess(title);
            this.fetchCategories().then(() => {
              this.categoryOptions = this.categoryList.slice();
            });
          });
          result.forEachLeft(({ title }) => {
            this.notifyFail(title);
          });
        });
    },
    /**
     * @description Обработать удаление шаблона протокола лечения.
     * @param {number} templateId
     * @returns {Promise<void>}
     */
    async onRemoveDiaryTemplate(templateId) {
      const dialog = await this.showRemoveDialog('Вы действительно хотите удалить шаблон?');
      if (dialog.result !== DialogResult.DELETE) {
        return;
      }
      this
        .$visitGateway
        .deleteDiaryTemplate(templateId)
        .then((result) => {
          result.forEach(({ title }) => {
            this.notifySuccess(title);
            this.fetchTemplates(this.categoryId);
          });
          result.forEachLeft(({ title }) => {
            this.notifyFail(title);
          });
        });
    },
    /**
     * @description Обработать отправку формы шаблона.
     * @param {DiaryTemplateCreationRequest} request
     * @returns {void}
     */
    onSubmitDiaryTemplate(request) {
      if (request.getId()) {
        this.onEditDiaryTemplate(request);
      } else {
        this.onCreateDiaryTemplate(request);
      }
    },
    /**
     * @description Обработать отправку формы категории.
     * @returns {void}
     */
    onSubmitDiaryTemplateCategory() {
      if (this.category.id) {
        this.onEditDiaryTemplateCategory();
      } else {
        this.onCreateDiaryTemplateCategory();
      }
    },
  },
  mixins: [
    notifierMixin,
    builtInDialogMixin,
  ],
  name: 'ClinicPanelDiaryTemplateList',
};
</script>
<style lang="scss">
@use "@/scss/buttons";
.v-expansion-panel-header--active {
  .diary-templates__edit__arrow {
    transform: rotate(180deg);
  }
}
.diary-templates-dialog {
  &__input-select {
    margin-top: 20px;
  }
}
.diary-templates {
  overflow-y: scroll;
  width: 100%;
  &__categories {
    width: 280px;
    &__caption {
      font-size: 14px;
      line-height: 17px;
      font-weight: 700;
      border-bottom: 2px solid #0489e0;
      padding: 13px 10px 14px;
    }
    &__container {
      display: flex;
      flex-direction: column;
      gap: 15px;
      width: 280px;
      background: #f2f7fa;
      padding: 20px 10px;
    }
  }
  &__empty-templates {
    text-align: center;
    padding-top: 40px;
    font-size: 24px;
  }
  &__container {
    width: 100%;
    padding: 38px 20px 48px 23px;
  }
  &__row {
    display: grid;
    grid-template-columns: 280px 1fr;
    gap: 10px;
  }
  &__header {
    display: flex;
    flex-flow: row wrap;
    justify-content: space-between;
    gap: 20px;
    margin: 0 0 37px 0;
  }
  &__table {
    width: 100%;
    &__head,
    &__row {
      display: grid;
      grid-template-columns: 1fr 43px 43px;
      align-items: center;
      font-size: 14px;
      line-height: 17px;
      &__name {
        text-align: left;
        margin-left: 10px;
        word-break: break-all;
      }
      &__edit {
        display: block;
        justify-self: center;
        background-image: url("@/assets/img/pen-grey.svg");
        background-size: contain;
        background-repeat: no-repeat;
        width: 14px;
        height: 14px;
        padding: 0;
        margin: 0;
        cursor: pointer;
        &:hover {
          background-image: url("@/assets/img/pen-blue.svg");
        }
      }
      &__delete {
        display: block;
        justify-self: center;
        background-image: url("@/assets/img/exit-grey.svg");
        background-size: contain;
        background-repeat: no-repeat;
        width: 12px;
        height: 12px;
        cursor: pointer;
        &:hover {
          background-image: url("@/assets/img/exit-blue.svg");
        }
      }
    }
    &__head {
      font-weight: 700;
      border-bottom: 2px solid #0489e0;
      padding: 13px 10px 14px;
    }
    &__row {
      width: 100%;
      border-bottom: 1px solid #eaeff3;
      padding: 12px 0;
      &:hover {
        background: #eaeff3;
      }
    }
  }
  &__title {
    font-weight: 600;
    font-size: 38px;
    line-height: 46px;
  }
  &__btn {
    @include buttons.btn-blue(100%, 48px, 16px);
    max-width: 200px;
  }
  &__list {
    display: flex;
    flex-direction: column;
    gap: 13px;
  }
  &__edit {
    background-color: #F2F5F8 !important;
    &__title {
      font-weight: 500;
      font-size: 24px;
      line-height: 29px;
    }
    .v-expansion-panel-header {
      padding: 21px 28px;
    }
    &__arrow {
      background-image: url("@/assets/img/checked/arrow-top-blue.svg");
      background-position: center;
      background-repeat: no-repeat;
      background-size: contain;
      width: 20px;
      height: 10px;
    }
    &__form {
      padding: 20px 2px 40px;
    }
    &__buttons {
      display: flex;
      flex-flow: row wrap;
      justify-content: flex-end;
      gap: 16px;
    }
    &__submit {
      @include buttons.btn-blue(207px, 48px, 16px);
    }
    &__remove {
      @include buttons.btn-transparent(207px, 48px, 16px);
    }
  }
  &__creation {
    padding: 26px 0 0 0;
    &__buttons {
      display: flex;
      flex-flow: row wrap;
      justify-content: flex-end;
      gap: 16px;
    }
    &.visit-diary-template__buttons {
      margin: 0 0 0 auto !important;
    }
    &__cancel {
      @include buttons.btn-transparent(214px, 56px, 20px);
    }
    &__submit {
      @include buttons.btn-blue(214px, 56px, 20px);
    }
  }
  &__filters {
    justify-content: flex-start;
    width: 100%;
    margin: 25px 0px;
    display: flex;
    flex-flow: row wrap;
    gap: 6px;
    &__item {
      display: flex;
      align-items: center;
      color: var(--reading-color);
      font-size: 14px;
      line-height: 17px;
      padding: 8px 15px;
      border: 1px solid #c2cfe0;
      border-radius: 4px;
      cursor: pointer;
      &:hover {
        border-color: var(--action-color);
        border-width: 2px;
        padding: 7px 14px;
      }
    }
    input:checked + &__item  {
      border-color: var(--action-color);
      border-width: 2px;
      padding: 7px 14px;
    }
  }
  &__modal {
    &__buttons {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-gap: 10px;
      margin: 10px 0 0 0;
    }
    &__button {
      @include buttons.btn-transparent(100%, 30px, 16px);
      &.negative {
        border: 1px solid rgba(224, 4, 4, 0.97);
        color: rgba(224, 4, 4, 0.97);
      }
    }
  }
}
@media screen and (max-width: 1320px) {
  .diary-templates {
    &__container {
      width: 100%;
    }
  }
}
@media screen and (max-width: 640px) {
  .diary-templates {
    &__container {
      padding: 38px 10px 140px;
    }
  }
}
</style>
